// Market amounts are kept in their original quote currency. A transfer is not a sale.
export const amount = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
export const priceText = (value, currency = 'BTC') => value === null || value === undefined ? 'Price unavailable' : `${Number(value).toLocaleString('en-US', { maximumFractionDigits: 8 })} ${currency}`;
export const txUrl = hash => /^[0-9a-f]{64}$/i.test(hash || '') ? `https://xchain.io/tx/${hash}` : 'https://xchain.io/asset/RAREPEPE';
export const HORIZON_URL = 'https://horizon.market/assets/RAREPEPE';
const quantityFor = (row, key, asset) => asset === 'BTC' && amount(row[key]) !== null ? amount(row[key]) / 1e8 : amount(row[`${key}_normalized`]);
export const MARKET_FILTERS = [{ value: 'sale', label: 'For sale · numbered cards' }, { value: 'emblem', label: 'OpenSea ask' }, { value: 'dispenser', label: 'In a dispenser' }, { value: 'order', label: 'DEX ask' }, { value: 'utxo', label: 'UTXO wrapped' }, { value: 'pool', label: 'In a liquidity pool' }, { value: 'destroyed', label: 'Burned cards' }];

export function openSeaAsk(ethereum, now = Date.now()) {
  const listing = ethereum?.listing;
  if (!ethereum?.ok || !listing?.ok || listing.available === false || (listing.ends && Number(listing.ends) * 1000 <= now)) return null;
  const raw = listing.price;
  const price = listing.unitPrice ?? (raw && Number.isInteger(raw.decimals) && raw.decimals >= 0 && raw.decimals <= 18 ? Number(raw.value) / 10 ** raw.decimals : null);
  if (!(price > 0) || !Number.isFinite(price)) return null;
  return { ...listing, kind: 'emblem', price, currency: listing.currency || raw?.currency, url: ethereum.url || 'https://opensea.io', lot: 1 };
}

export function matchEmblemAsk(locations, ethereum, now = Date.now()) {
  const ask = openSeaAsk(ethereum, now), a = ask?.attribution;
  // Public-page coverage is the best ask only. Ownership is verified; execution is not.
  if (!a?.ok || a.method !== 'single_owned_serial' || a.owner !== ask.seller || !ask.id || !ask.ends || !ethereum.observedAt || now - Date.parse(ethereum.observedAt) > 90000 || now - Date.parse(a.checkedAt) > 90000 || !Number.isFinite(Date.parse(a.checkedAt)) || !Number.isFinite(Date.parse(ethereum.observedAt))) return {};
  const serials = Object.entries(locations || {}).filter(([, l]) => l.kind === 'address' && l.id === a.bitcoinAddress).map(([id]) => Number(id));
  if (serials.length !== 1) return {};
  return { [serials[0]]: [{ ...ask, id: `opensea:${ask.id}`, serials }] };
}

export function normalizeDispenser(row) {
  const lot = amount(row.give_quantity_normalized), remaining = amount(row.give_remaining_normalized);
  // satoshirate is a fiat amount for oracle dispensers; use Core's resolved price only.
  const btc = amount(row.satoshi_price_normalized);
  if (row.asset !== 'RAREPEPE' || ![0, 1].includes(Number(row.status)) || !(lot > 0) || !(remaining >= lot)) return null;
  return { id: row.tx_hash, kind: 'dispenser', currency: 'BTC', price: btc > 0 ? btc / lot : null, lot, remaining, block: row.block_index, url: `https://xchain.io/tx/${row.tx_hash}`, address: row.source };
}
export function normalizeOrder(row) {
  const give = quantityFor(row, 'give_quantity', row.give_asset), get = quantityFor(row, 'get_quantity', row.get_asset);
  const left = amount(row.give_remaining_normalized), wanted = amount(row.get_remaining_normalized);
  if (row.status !== 'open' || !(give > 0 && get > 0 && left > 0 && wanted > 0)) return null;
  const ask = row.give_asset === 'RAREPEPE';
  if (!ask && row.get_asset !== 'RAREPEPE') return null;
  // BTC bids on the Counterparty DEX are promises to pay, not escrowed BTC.
  return { id: row.tx_hash, kind: 'order', side: ask ? 'ask' : 'bid', currency: ask ? row.get_asset : row.give_asset, price: ask ? get / give : give / get, remaining: ask ? left : wanted, lot: 1, block: row.block_index, expires: row.expire_index, funded: row.give_asset !== 'BTC', url: txUrl(row.tx_hash) };
}
export function normalizeHorizon(row, now = Date.now()) {
  if (row.asset_name !== 'RAREPEPE' || row.listing_type !== 'counterparty' || !row.funded || row.filled || !row.confirmed || row.pending || row.delisted || row.seller_delisted || row.expired || row.anomalous || row.blacklisted || !(Date.parse(row.expires_at) > now)) return null;
  const lot = amount(row.asset_quantity), sats = amount(row.price);
  if (!(lot > 0 && sats > 0) || !/^[0-9a-f]{64}:\d+$/.test(row.asset_utxo_id || '')) return null;
  return { id: row.id, outpoint: row.asset_utxo_id, kind: 'utxo', currency: 'BTC', price: sats / 1e8 / lot, lot, remaining: lot, block: row.block_index, expiresAt: row.expires_at, url: HORIZON_URL };
}
export function matchListings(locations, listings) {
  const result = {};
  for (const listing of listings) {
    const serials = Object.entries(locations || {}).filter(([, l]) => l.kind === listing.kind && l.id === (listing.outpoint || listing.id)).map(([id]) => Number(id));
    // Do not attach a current offer to stale or only partially projected inventory.
    if (serials.length !== listing.remaining) continue;
    for (const serial of serials) (result[serial] ||= []).push({ ...listing, serials });
  }
  return result;
}
export function normalizeTrade(row, kind) {
  let quantity, paid, currency, id, tx;
  if (kind === 'dispenses') {
    if (row.asset !== 'RAREPEPE') return null;
    quantity = amount(row.dispense_quantity_normalized); paid = amount(row.btc_amount_normalized); currency = 'BTC';
    id = `${row.tx_hash}:${row.dispense_index}`; tx = row.tx_hash;
  } else {
    if (kind === 'matches' && row.status !== 'completed') return null;
    const forward = row.forward_asset === 'RAREPEPE';
    if (!forward && row.backward_asset !== 'RAREPEPE') return null;
    quantity = amount(row[forward ? 'forward_quantity_normalized' : 'backward_quantity_normalized']);
    currency = row[forward ? 'backward_asset' : 'forward_asset'];
    paid = quantityFor(row, forward ? 'backward_quantity' : 'forward_quantity', currency);
    id = row.id || `${row.tx_hash}:${row.match_index}`; tx = row.tx1_hash || row.tx_hash;
  }
  if (!(quantity > 0 && paid > 0) || !currency) return null;
  return { id: `${kind}:${id}`, reference: row.id || row.tx_hash, dispenserId: row.dispenser_tx_hash, tx, kind, quantity, paid, currency, price: paid / quantity, time: row.block_time, block: row.block_index, url: txUrl(tx) };
}
export function tradeForMovement(movement, trades) {
  const candidates = trades.filter(t => t.block === movement.block && (t.kind === 'dispenses'
    ? movement.reason === 'dispense' && t.tx === movement.tx_hash && t.dispenserId === movement.from?.id
    : movement.reason === 'order match' && t.kind === 'matches' && t.reference === movement.event_reference));
  return candidates.length === 1 ? candidates[0] : null;
}
export function tradeStats(trades) {
  const currencies = {};
  for (const trade of trades) {
    const item = currencies[trade.currency] ||= { currency: trade.currency, trades: 0, quantity: 0, volume: 0, last: null };
    item.trades++; item.quantity += trade.quantity; item.volume += trade.paid;
    if (!item.last || trade.time > item.last.time) item.last = trade;
  }
  return Object.values(currencies).sort((a, b) => a.currency.localeCompare(b.currency));
}
export function normalizeOpenSeaSale(event) {
  const quantity = amount(event.quantity), paid = amount(event.payment?.quantity);
  const decimals = event.payment?.decimals;
  const tx = typeof event.transaction === 'string' ? event.transaction : event.transaction?.hash;
  if (event.event_type !== 'sale' || !(quantity > 0 && paid > 0) || !Number.isInteger(decimals) || decimals < 0 || decimals > 18 || !/^0x[0-9a-f]{64}$/i.test(tx || '') || !event.payment?.symbol) return null;
  return { id: event.order_hash || tx, quantity, price: paid / 10 ** decimals / quantity, currency: event.payment.symbol, time: amount(event.closing_date), url: `https://etherscan.io/tx/${tx}` };
}
export function filterMarket(id, filter, locations, listings) {
  if (!filter) return true;
  if (filter === 'sale') return !!listings[id]?.length;
  if (filter === 'dispenser' || filter === 'order' || filter === 'emblem') return listings[id]?.some(l => l.kind === filter) || false;
  return locations[id]?.kind === filter;
}

export async function loadHistory(rpc, type, status = 'all') {
  const rows = [], seen = new Set();
  let expected;
  for (let offset = 0; offset < 2000;) {
    const page = await rpc('get_asset_history', { asset: 'RAREPEPE', type, status, offset, limit: 200 });
    if (!page.ok || !Array.isArray(page.result) || page.asset !== 'RAREPEPE' || page.type !== type || !Number.isInteger(page.total)) throw new Error('market_source_unavailable');
    if (expected !== undefined && page.total !== expected) throw new Error('market_changed_during_pagination');
    expected = page.total;
    for (const row of page.result) {
      const key = row.id || `${row.tx_hash}:${row.dispense_index ?? row.match_index ?? ''}`;
      if (seen.has(key)) throw new Error('market_changed_during_pagination');
      seen.add(key); rows.push(row);
    }
    offset += page.result.length;
    if (!page.has_more) {
      if (offset !== expected) throw new Error('incomplete_market_history');
      return rows;
    }
    if (!page.result.length) throw new Error('incomplete_market_history');
  }
  throw new Error('market_history_limit');
}
